import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    brand: '',
    message: '',
    projectTypes: [] as string[]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const projectTypeOptions = ['Product Review', 'Unboxing', 'Tutorial', 'Lifestyle'];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('brand', formData.brand);
      formBody.append('message', formData.message);
      formBody.append('projectTypes', formData.projectTypes.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d6e02raohb161tfd8pbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          brand: '',
          message: '',
          projectTypes: []
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (option: string) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(option)
        ? prev.projectTypes.filter(t => t !== option)
        : [...prev.projectTypes, option]
    }));
  };

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-5 bg-gradient-to-br from-[#00D9FF] to-[#00B8D4] p-6 sm:p-10 md:p-16 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-noise"></div>
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-2 sm:mb-4 leading-tight">
                Let&apos;s Create Together
              </h2>
              <p className="text-white/90 text-sm sm:text-lg md:text-xl font-light mb-6 sm:mb-12">
                Ready to elevate your brand with authentic content?
              </p>

              <div className="space-y-3 sm:space-y-6 mb-6 sm:mb-12">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                    <i className="ri-mail-line text-white text-lg sm:text-2xl"></i>
                  </div>
                  <span className="text-white text-sm sm:text-lg break-all">james@jamesmoore.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                    <i className="ri-phone-line text-white text-lg sm:text-2xl"></i>
                  </div>
                  <span className="text-white text-sm sm:text-lg">+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                    <i className="ri-map-pin-line text-white text-lg sm:text-2xl"></i>
                  </div>
                  <span className="text-white text-sm sm:text-lg">Los Angeles, CA</span>
                </div>
              </div>

              <div>
                <p className="text-white text-xs sm:text-sm uppercase tracking-wider mb-3 font-medium">
                  Connect With Me
                </p>
                <div className="flex gap-2.5 sm:gap-4 flex-wrap">
                  {[
                    { icon: 'ri-instagram-line', url: 'https://instagram.com' },
                    { icon: 'ri-tiktok-line', url: 'https://tiktok.com' },
                    { icon: 'ri-youtube-line', url: 'https://youtube.com' },
                    { icon: 'ri-linkedin-line', url: 'https://linkedin.com' },
                    { icon: 'ri-twitter-x-line', url: 'https://twitter.com' },
                    { icon: 'ri-mail-line', url: 'mailto:james@jamesmoore.com' }
                  ].map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-9 h-9 sm:w-12 sm:h-12 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#00D9FF] transition-all duration-300 cursor-pointer"
                    >
                      <i className={`${social.icon} text-base sm:text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-7 bg-white p-6 sm:p-10 md:p-16">
            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center mb-4 sm:mb-6 animate-bounce">
                  <i className="ri-check-line text-3xl sm:text-4xl text-green-600"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-3 sm:mb-4">Message Sent!</h3>
                <p className="text-gray-600 text-base sm:text-lg">
                  Thanks for reaching out. I&apos;ll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6 sm:mb-8">Send a Message</h3>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-xs sm:text-sm uppercase tracking-wider text-gray-700 mb-1.5 sm:mb-2 font-medium">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full h-12 sm:h-14 px-4 text-sm sm:text-base rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                        errors.name
                          ? 'border-red-500'
                          : formData.name
                          ? 'border-green-500'
                          : 'border-gray-300 focus:border-[#00D9FF]'
                      }`}
                      placeholder="John Doe"
                    />
                    {errors.name && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-xs sm:text-sm uppercase tracking-wider text-gray-700 mb-1.5 sm:mb-2 font-medium">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full h-12 sm:h-14 px-4 text-sm sm:text-base rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                        errors.email
                          ? 'border-red-500'
                          : formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
                          ? 'border-green-500'
                          : 'border-gray-300 focus:border-[#00D9FF]'
                      }`}
                      placeholder="john@example.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Brand Name Field */}
                  <div>
                    <label className="block text-xs sm:text-sm uppercase tracking-wider text-gray-700 mb-1.5 sm:mb-2 font-medium">
                      Brand Name
                    </label>
                    <input
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full h-12 sm:h-14 px-4 text-sm sm:text-base rounded-xl border-2 border-gray-300 focus:border-[#00D9FF] transition-all duration-300 focus:outline-none"
                      placeholder="Your Brand"
                    />
                  </div>

                  {/* Project Type Checkboxes */}
                  <div>
                    <label className="block text-xs sm:text-sm uppercase tracking-wider text-gray-700 mb-2 sm:mb-3 font-bold">
                      Project Type
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {projectTypeOptions.map((option) => (
                        <label
                          key={option}
                          className="flex items-center gap-2 sm:gap-3 cursor-pointer group"
                        >
                          <div className="relative">
                            <input
                              type="checkbox"
                              name="projectTypes"
                              value={option}
                              checked={formData.projectTypes.includes(option)}
                              onChange={() => handleCheckboxChange(option)}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 sm:w-6 sm:h-6 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                                formData.projectTypes.includes(option)
                                  ? 'bg-[#00D9FF] border-[#00D9FF]'
                                  : 'border-gray-300 group-hover:border-[#00D9FF]'
                              }`}
                            >
                              {formData.projectTypes.includes(option) && (
                                <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                              )}
                            </div>
                          </div>
                          <span className="text-gray-700 text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-xs sm:text-sm uppercase tracking-wider text-gray-700 mb-1.5 sm:mb-2 font-medium">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      rows={5}
                      className={`w-full px-4 py-3 text-sm sm:text-base rounded-xl border-2 transition-all duration-300 focus:outline-none resize-none ${
                        errors.message
                          ? 'border-red-500'
                          : formData.message
                          ? 'border-green-500'
                          : 'border-gray-300 focus:border-[#00D9FF]'
                      }`}
                      placeholder="Tell me about your project..."
                    ></textarea>
                    <div className="flex justify-between items-center mt-1">
                      {errors.message && <p className="text-red-500 text-xs sm:text-sm">{errors.message}</p>}
                      <p className={`text-xs sm:text-sm ml-auto ${formData.message.length > 500 ? 'text-red-500' : 'text-gray-500'}`}>
                        {formData.message.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 sm:h-16 bg-black text-white rounded-full font-bold uppercase tracking-wider text-sm sm:text-base flex items-center justify-center gap-3 hover:bg-[#00D9FF] hover:text-black transition-all duration-300 group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg sm:text-xl"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Escaped closing tags in the SVG data URL to avoid JSX parsing errors */}
      <style>{`
        .bg-noise {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C\\/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C\\/svg%3E");
        }
      `}</style>
    </section>
  );
}
